import { CacheHelper } from "../../../utilities/CacheHelper";
import { DocubirdCache } from "../config/constants";

// Sole owner of the selected-email header cache that drives the email-to-
// metadata mapping. Three producers write the same data (OutlookService
// multi-select loads, ExchangeService.getSelectedExchangeEmails, and the
// content-manager copy flow) and ~6 consumers read it; routing every write
// through this store gives all of them the same two guarantees:
//
// 1. Stale writes never land: every publish/clear advances a generation, and
//    an async load (beginLoad) may only publish while its generation is still
//    current. A load that lost the race publishes nothing — an empty cache
//    degrades safely to unmapped fields, while another selection's headers
//    would persist wrong metadata.
// 2. Readers can await an in-flight load (getAsync) instead of misreading the
//    transient empty cache as "nothing mapped": the metadata form reads the
//    cache exactly once while it is built.
//
// The invariant on the data itself: the published array holds EXACTLY one
// entry per selected message ({...headers, id} or an {id} stub), because
// consumers infer single-vs-multi selection from its length.

/**
 * The mailbox the current multi-select selection lives in, as probed from one
 * selected item's shared properties. Absent for an own-mailbox selection.
 * The save payload needs it because Office.context.mailbox.item is null in
 * multi-select, so the item-based shared-mailbox detection cannot run at save
 * time; it carries the same staleness guarantees as the cached headers.
 */
export interface ISelectedMailboxTarget {
    targetMailbox: string;
    targetRestUrl?: string;
}

export interface ISelectedExchangeItemsLoad {
    /**
     * Publishes the loaded headers (and, for a shared selection, the probed
     * mailbox target) unless a newer load/publish/clear superseded this load.
     * unresolvedSharedSelection marks a selection PROVEN to live outside the
     * user's own mailbox whose owner could not be identified: a save would
     * fail for every file, so the save UI blocks on this flag instead.
     */
    publish(items: any[], mailboxTarget?: ISelectedMailboxTarget, unresolvedSharedSelection?: boolean): void;
    /** Retires this load's pending marker and releases awaiting readers. MUST be called exactly once (finally). */
    finish(): void;
}

/** Total wall-clock bound for getAsync across however many loads replace each other. */
const pendingWaitTimeoutMs: number = 30000;

class SelectedExchangeItemsStore {
    private generation: number = 0;

    /**
     * Starts an async load: clears the current items synchronously (they
     * describe a selection that is being replaced) and publishes a pending
     * marker so readers wait for this load instead of seeing a transient
     * empty cache. Call finish() in a finally — a load that never finishes
     * would otherwise stall every reader for the full timeout.
     */
    beginLoad(): ISelectedExchangeItemsLoad {
        const loadGeneration: number = ++this.generation;
        let releaseAwaiters: () => void;
        const pendingLoad = new Promise<void>(resolve => { releaseAwaiters = resolve; });

        CacheHelper.remove(DocubirdCache.selectedExchangeItems);
        CacheHelper.remove(DocubirdCache.selectedExchangeMailboxTarget);
        CacheHelper.remove(DocubirdCache.selectedExchangeMailboxUnresolved);
        CacheHelper.set(DocubirdCache.selectedExchangeItemsPending, pendingLoad);

        return {
            publish: (items: any[], mailboxTarget?: ISelectedMailboxTarget, unresolvedSharedSelection?: boolean): void => {
                if (this.generation === loadGeneration) {
                    CacheHelper.set(DocubirdCache.selectedExchangeItems, items);
                    if (mailboxTarget?.targetMailbox) {
                        CacheHelper.set(DocubirdCache.selectedExchangeMailboxTarget, mailboxTarget);
                    }
                    if (unresolvedSharedSelection === true) {
                        CacheHelper.set(DocubirdCache.selectedExchangeMailboxUnresolved, true);
                    }
                }
            },
            finish: (): void => {
                // Retire the pending marker (only our own — a newer load may have
                // replaced it) BEFORE releasing awaiters, so they re-read a
                // settled cache state.
                if (CacheHelper.get(DocubirdCache.selectedExchangeItemsPending) === pendingLoad) {
                    CacheHelper.remove(DocubirdCache.selectedExchangeItemsPending);
                }
                releaseAwaiters();
            }
        };
    }

    /**
     * Synchronous publish for producers that already hold the headers (the
     * content-manager copy flow). Advances the generation so any in-flight
     * async load that started earlier can no longer overwrite this data.
     */
    set(items: any[]): void {
        this.generation++;
        CacheHelper.set(DocubirdCache.selectedExchangeItems, items);
        // The direct producers (content-manager flows) carry their mailbox as
        // exchangeAccountEmail, not as a probed target: a leftover target from
        // an Outlook multi-select load must not outlive that selection.
        CacheHelper.remove(DocubirdCache.selectedExchangeMailboxTarget);
        CacheHelper.remove(DocubirdCache.selectedExchangeMailboxUnresolved);
    }

    /** Clears the items and invalidates any in-flight load. */
    clear(): void {
        this.generation++;
        CacheHelper.remove(DocubirdCache.selectedExchangeItems);
        CacheHelper.remove(DocubirdCache.selectedExchangeMailboxTarget);
        CacheHelper.remove(DocubirdCache.selectedExchangeMailboxUnresolved);
    }

    get(): any[] {
        return CacheHelper.get(DocubirdCache.selectedExchangeItems);
    }

    getMailboxTarget(): ISelectedMailboxTarget | undefined {
        return CacheHelper.get(DocubirdCache.selectedExchangeMailboxTarget);
    }

    /**
     * True when the current selection was PROVEN to live outside the user's
     * own mailbox but no owning shared mailbox could be identified: every
     * file of a save would fail server-side, so the save UI blocks on this.
     */
    isUnresolvedSharedSelection(): boolean {
        return CacheHelper.get(DocubirdCache.selectedExchangeMailboxUnresolved) === true;
    }

    /**
     * Resolves the probed mailbox target of the current selection, waiting for
     * an in-flight load exactly like getAsync: reading mid-load would return
     * undefined and silently degrade a shared-mailbox save to /me.
     */
    async getMailboxTargetAsync(): Promise<ISelectedMailboxTarget | undefined> {
        await this.awaitPendingLoad();
        return this.getMailboxTarget();
    }

    /**
     * Resolves the cached per-message headers, waiting for an in-flight load
     * instead of misreading the transient empty cache as "nothing mapped".
     * Re-checks after each wait (a new selection can start another load while
     * awaiting the previous one) under one wall-clock deadline — a safety net
     * only: the wait degrades to reading the cache as-is (unmapped fields),
     * never hangs the metadata form on a load whose callback never fires.
     */
    async getAsync(): Promise<any[]> {
        await this.awaitPendingLoad();
        return this.get();
    }

    /** Shared wait: blocks readers while a load is in flight, bounded by one wall-clock deadline. */
    private async awaitPendingLoad(): Promise<void> {
        const waitDeadline: number = Date.now() + pendingWaitTimeoutMs;
        for (; ;) {
            const pendingLoad = CacheHelper.get(DocubirdCache.selectedExchangeItemsPending);
            const remainingMs: number = waitDeadline - Date.now();
            if (!pendingLoad || remainingMs <= 0) {
                break;
            }
            let timeoutHandle: ReturnType<typeof setTimeout>;
            try {
                await Promise.race([
                    pendingLoad,
                    new Promise<void>(resolve => { timeoutHandle = setTimeout(resolve, remainingMs); })
                ]);
            } catch {
                // A failed load reads as an empty cache below.
            } finally {
                clearTimeout(timeoutHandle);
            }
            if (CacheHelper.get(DocubirdCache.selectedExchangeItemsPending) === pendingLoad) {
                break;
            }
        }
    }
}

export const selectedExchangeItemsStore = new SelectedExchangeItemsStore();
